import type { AudioSegmentMeta } from '@shared/contract';
import { api, ApiError } from './api';

/**
 * Lokal kö för ljudsegment i IndexedDB (avsnitt 4.2 och 4.3).
 * Ett segment ligger kvar lokalt tills backend bekräftat mottagningen.
 * Observera: webbläsarens lagring är ingen garanti om sidan stängs eller
 * operativsystemet avslutar webbläsaren.
 */

export interface QueuedSegment {
  key: string;
  meta: AudioSegmentMeta;
  data: ArrayBuffer;
  attempts: number;
  /** Satt när servern permanent avvisat segmentet. */
  rejected?: string;
  queued_at: number;
}

// Behåller namnet från tiden som Meeting Robot: ett nytt namn skulle tappa ljud som ännu inte laddats upp.
const DB_NAME = 'meeting-robot';
const STORE = 'segments';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('meeting', 'meta.meeting_id');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  dbPromise ??= openDb();
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return db().then(
    (d) =>
      new Promise<T>((resolve, reject) => {
        const t = d.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        t.oncomplete = () => resolve(req.result);
        t.onerror = () => reject(t.error ?? req.error);
        t.onabort = () => reject(t.error ?? new Error('IndexedDB avbröts'));
      }),
  );
}

function segmentKey(meta: Pick<AudioSegmentMeta, 'meeting_id' | 'source_id' | 'sequence_number'>): string {
  return `${meta.meeting_id}:${meta.source_id}:${String(meta.sequence_number).padStart(6, '0')}`;
}

export async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface QueueState {
  pending: number;
  rejected: number;
  uploading: boolean;
  online: boolean;
  lastError: string | null;
  storageError: string | null;
  confirmed: number;
}

type Listener = (state: QueueState) => void;

/** Laddar upp segment i ordning med återförsök och exponentiell väntan. */
export class UploadQueue {
  private state: QueueState = {
    pending: 0,
    rejected: 0,
    uploading: false,
    online: navigator.onLine,
    lastError: null,
    storageError: null,
    confirmed: 0,
  };
  private listeners = new Set<Listener>();
  private running = false;
  private backoffMs = 1000;
  private timer: number | null = null;
  /** Används om IndexedDB inte fungerar, t.ex. i privat läge. */
  private memory = new Map<string, QueuedSegment>();
  private useMemory = false;

  constructor() {
    window.addEventListener('online', () => {
      this.set({ online: true });
      this.backoffMs = 1000;
      this.kick();
    });
    window.addEventListener('offline', () => this.set({ online: false }));
    void this.refresh().then(() => this.kick());
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  getState(): QueueState {
    return this.state;
  }

  private set(patch: Partial<QueueState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((fn) => fn(this.state));
  }

  async enqueue(meta: Omit<AudioSegmentMeta, 'checksum'>, data: ArrayBuffer): Promise<void> {
    const checksum = await sha256Hex(data);
    const item: QueuedSegment = {
      key: segmentKey(meta),
      meta: { ...meta, checksum },
      data,
      attempts: 0,
      queued_at: Date.now(),
    };
    if (!this.useMemory) {
      try {
        await tx('readwrite', (s) => s.put(item));
        this.set({ storageError: null });
      } catch (err) {
        this.useMemory = true;
        this.set({
          storageError:
            'Lokal lagring fungerar inte. Ljud hålls bara i minnet tills det laddats upp och går förlorat om sidan stängs.',
        });
      }
    }
    if (this.useMemory) this.memory.set(item.key, item);
    await this.refresh();
    this.kick();
  }

  private async all(): Promise<QueuedSegment[]> {
    let items: QueuedSegment[] = [];
    if (!this.useMemory) {
      try {
        items = await tx('readonly', (s) => s.getAll() as IDBRequest<QueuedSegment[]>);
      } catch {
        this.useMemory = true;
      }
    }
    items = items.concat([...this.memory.values()]);
    return items.sort((a, b) => a.queued_at - b.queued_at || a.key.localeCompare(b.key));
  }

  private async remove(key: string): Promise<void> {
    this.memory.delete(key);
    if (!this.useMemory) await tx('readwrite', (s) => s.delete(key)).catch(() => undefined);
  }

  private async save(item: QueuedSegment): Promise<void> {
    if (this.memory.has(item.key) || this.useMemory) this.memory.set(item.key, item);
    else await tx('readwrite', (s) => s.put(item)).catch(() => undefined);
  }

  async refresh(): Promise<void> {
    const items = await this.all();
    this.set({
      pending: items.filter((i) => !i.rejected).length,
      rejected: items.filter((i) => i.rejected).length,
    });
  }

  /** Antal segment som väntar för ett visst möte. */
  async pendingFor(meetingId: string): Promise<number> {
    return (await this.all()).filter((i) => i.meta.meeting_id === meetingId && !i.rejected).length;
  }

  /** Väntar tills kön för mötet är tom, eller tills tidsgränsen nås. */
  async drain(meetingId: string, timeoutMs: number): Promise<boolean> {
    const end = Date.now() + timeoutMs;
    this.backoffMs = 1000;
    this.kick();
    while (Date.now() < end) {
      if ((await this.pendingFor(meetingId)) === 0) return true;
      await new Promise((r) => setTimeout(r, 500));
    }
    return (await this.pendingFor(meetingId)) === 0;
  }

  async discardRejected(): Promise<void> {
    for (const item of await this.all()) if (item.rejected) await this.remove(item.key);
    await this.refresh();
  }

  kick(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.running) void this.run();
  }

  private async run(): Promise<void> {
    this.running = true;
    this.set({ uploading: true });
    try {
      for (;;) {
        const next = (await this.all()).find((i) => !i.rejected);
        if (!next) break;
        const outcome = await this.upload(next);
        if (outcome === 'retry') {
          this.timer = window.setTimeout(() => this.kick(), this.backoffMs);
          this.backoffMs = Math.min(this.backoffMs * 2, 30_000);
          break;
        }
        this.backoffMs = 1000;
      }
    } finally {
      this.running = false;
      this.set({ uploading: false });
      await this.refresh();
    }
  }

  private async upload(item: QueuedSegment): Promise<'ok' | 'retry' | 'rejected'> {
    const form = new FormData();
    for (const [k, v] of Object.entries(item.meta)) if (v !== undefined) form.append(k, String(v));
    form.append('audio', new Blob([item.data], { type: item.meta.codec }), 'segment');
    try {
      await api.uploadSegment(item.meta.meeting_id, form);
      await this.remove(item.key);
      this.set({ lastError: null, online: true, confirmed: this.state.confirmed + 1 });
      return 'ok';
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 404) {
        // Mötet är raderat. Segmentet kan inte längre användas.
        await this.remove(item.key);
        return 'ok';
      }
      if (status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429) {
        item.rejected = (err as Error).message;
        await this.save(item);
        this.set({ lastError: `Ett segment avvisades av servern: ${item.rejected}` });
        return 'rejected';
      }
      item.attempts++;
      await this.save(item);
      this.set({
        lastError: status === 401 ? 'Du är utloggad. Logga in igen för att ladda upp ljud.' : 'Kunde inte nå servern. Försöker igen.',
        online: status !== 0,
      });
      return 'retry';
    }
  }
}

export const uploadQueue = new UploadQueue();
